import React, {useState} from "react";
import {Controller, FieldValues, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {useTranslation} from "@twigsby/frontend-framework";
import AddressImporterForm from "../Components/AddressImporterForm";
import {AddressImporterModalType} from "../AddressImportTypes";
import {AlertBanner, ButtonSubmit, ColourSolid, Modal, ModalSize, UploadFile} from "@twigsby/frontend-framework";

enum HeaderNameMap {
    Name = 'name',
    BusinessName = 'businessName',
    PropertyNumber = 'propertyName',
    Street = 'addressLine1',
    City = 'addressLine2',
    Locality = 'addressLine3',
    PostCode = 'postCode',
    CountryName = 'countryName',
    CountryCode = 'countryCode',
    EmailAddress = 'emailAddress',
    PhoneNumber = 'phoneNo'
}

const ImportCsvModal = (props: AddressImporterModalType) => {
    const { t } = useTranslation();

    const [ importing, setImporting ] = useState<boolean>(false);
    const [ parsedData, setParsedData ] = useState<any>(null);

    const [ parseError, setParseError ] = useState<any>({ state: false, title: '', message: ''});

    const {
        handleSubmit,
        getValues,
        control,
    } = useForm({
        defaultValues: {
            file: null,
        }
    });

    const MapHeaderValues = (row: string) => {
        const data: Array<string> = SanitiseRow(row);

        for(let i = 0; i < data.length; i++) {
            // @ts-ignore
            data[i] = HeaderNameMap[data[i]];
        }

        return data;
    }

    const SanitiseRow = (row: string) : Array<string> => {
        const values = row.split(',');

        for(let i = 0; i < values.length; i++) {
            values[i] = values[i].replace(/"/g, '').trim();
        }

        return values;
    }

    const ParseCsv = (input: any) => {
        const data = input.replace(/\n\r/, '\n').trim().split('\n');

        const requiredHeadersLength = Object.keys(HeaderNameMap).length;

        const headers = MapHeaderValues(data[0]);
        const MissingHeaderNames: Array<string> = Array.from(Object.keys(HeaderNameMap)).filter((header) => data[0].indexOf(header) === -1 );

        data.splice(0, 1);

        const MappedData: any = [];

        data.forEach((line: any) => {
            const lineValues = SanitiseRow(line.trim());
            const emptyValues = [];

            for (let i = 0; i < lineValues.length; i++) {
                emptyValues[i] = lineValues[i] === '';
            }

            if(lineValues.length === requiredHeadersLength && !emptyValues.every((item: boolean) => item)) {
                let address: Record<any, any> = {};

                for (let i = 0; i < lineValues.length; i++) {
                    address[headers[i]] = lineValues[i]?.trim();
                }

                MappedData.push(address);
            }
        });

        setImporting(false);

        if(MappedData.length > 0) {
            setParseError({ state: false, title: '', message: '' });
            if(props.forwardData && props.actionedCallback) {
                props.actionedCallback(MappedData);
            } else {
                setParsedData(MappedData);
            }
        } else {
            if(headers.length !== requiredHeadersLength) {
                setParseError({
                    state: true,
                    title: t('address.csv.validation.missingColumns.title'),
                    message: t('address.csv.validation.missingColumns.missingColumns', { count: MissingHeaderNames.length, columns: MissingHeaderNames.map((header) =>  header) })
                });
            } else {
                setParseError({
                    state: true,
                    title: t('address.csv.validation.noData.title'),
                    message: t('address.csv.validation.noData.description')
                });
            }
        }

    }

    const ReadLocalFile = (data: any) => {
        const reader = new FileReader();

        reader.onload = () => {
            ParseCsv(reader.result);
        }

        reader.readAsBinaryString(data.file[0])
    }

    const HandleImport = (data: FieldValues, e: any) => {
        setImporting(true);
        ReadLocalFile(data);
    }

    const HandleSave = (data: any) => {
        if(props.actionedCallback) {
            props.actionedCallback(data);
        }
    }

    return (
        <Modal title={t('address.csv.title')} isOpen={props.isOpen} closeCallback={() => props.closeCallback()} modalSize={ModalSize.ExtraLarge}>
            {!parsedData && (
                <form onSubmit={handleSubmit((data, e) => HandleImport(data, e))}>
                    <fieldset className={'flex-column flex-gap-md'}>

                        <AlertBanner
                            title={t('address.csv.dataTitle')}
                            content={t('address.csv.dataDescription')}
                            addOn={<a className={'btn primary'} download href={`${process.env.PUBLIC_URL}/UKPostboxAddressImportTemplate.csv`}>{t('address.csv.actions.download')}</a>}
                        />

                        <Controller
                            control={control}
                            name="file"
                            rules={{ required: t('address.csv.validation.noFile') }}
                            render={({field}) =>
                                <UploadFile
                                    onChange={(e: any) => field.onChange(e)}
                                    accept={'.csv'}
                                    extensionDisplayValue={'CSV'}
                                    singleFileOnly={true}
                                    componentError={parseError}
                                />
                            }
                        />

                        <ButtonSubmit
                            loading={importing}
                            disabled={importing}
                            title={t('address.csv.actions.import')}
                            fullWidth={true}
                            colour={ColourSolid.Primary}
                        />

                    </fieldset>
                </form>
            )}

            {parsedData && (
                <AddressImporterForm
                    addressData={parsedData}
                    embeddedForm={true}
                    savedCallback={(data: any) => HandleSave(data)}
                    removeCallback={() => setParsedData(null)}
                />
            )}

        </Modal>
    )
}

export default ImportCsvModal
